import { lazy, ComponentType } from "react";
import { isNativeRuntime } from "@/lib/nativeRuntime";

/**
 * Wraps React.lazy() with automatic retry + cache-busting for failed dynamic imports.
 * When a deploy changes chunk hashes, old chunk URLs 404. This retries the import
 * up to 3 times, appending a cache-busting timestamp to force fresh module resolution.
 * After all retries fail, web does a hard page reload as a last resort.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = isNativeRuntime() ? 6 : 3
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(importFn, retries));
}

function extractFailedModuleUrl(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error ?? "");

  const absoluteMatch = message.match(/https?:\/\/[^\s)"']+\.js(?:\?[^\s)"']*)?/i);
  if (absoluteMatch) return absoluteMatch[0];

  const relativeMatch = message.match(/\/assets\/[^\s)"']+\.js(?:\?[^\s)"']*)?/i);
  if (relativeMatch && typeof window !== "undefined") {
    return new URL(relativeMatch[0], window.location.origin).toString();
  }

  return null;
}

async function tryCacheBustedNativeImport<T extends ComponentType<any>>(
  error: unknown
): Promise<{ default: T } | null> {
  if (!isNativeRuntime() || typeof window === "undefined") return null;

  const failedModuleUrl = extractFailedModuleUrl(error);
  if (!failedModuleUrl) return null;

  try {
    const parsed = new URL(failedModuleUrl, window.location.href);
    if (parsed.origin !== window.location.origin) return null;

    const cacheBustedUrl = `${parsed.toString()}${parsed.search ? "&" : "?"}_cb=${Date.now()}`;
    console.warn("[lazyWithRetry] Native fallback import with cache-bust:", cacheBustedUrl);

    const recovered = await import(/* @vite-ignore */ cacheBustedUrl);
    return recovered as { default: T };
  } catch (fallbackError) {
    console.error("[lazyWithRetry] Native cache-busted import failed", fallbackError);
    return null;
  }
}

async function retryImport<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retriesLeft: number
): Promise<{ default: T }> {
  try {
    return await importFn();
  } catch (error) {
    if (retriesLeft <= 0) {
      if (isNativeRuntime()) {
        const recoveredModule = await tryCacheBustedNativeImport<T>(error);
        if (recoveredModule) return recoveredModule;
      }

      // Last resort: single hard reload to get fresh index.html with correct chunk refs
      // Works identically on web and native — sessionStorage flag prevents loops
      const reloadKey = "__gistgift_chunk_reload";
      const hasReloaded = sessionStorage.getItem(reloadKey);

      if (!hasReloaded) {
        sessionStorage.setItem(reloadKey, "1");
        const target = isNativeRuntime()
          ? `${window.location.origin}/?_cb=${Date.now()}#/boot`
          : `${window.location.origin}/?_cb=${Date.now()}`;
        window.location.href = target;
        return new Promise(() => {});
      }

      // Already reloaded once and still failing — clear flag and let error propagate
      sessionStorage.removeItem(reloadKey);
      throw error;
    }

    console.warn(
      `[lazyWithRetry] Import failed, retrying (${retriesLeft} left)...`,
      error
    );

    // Longer delay on native to handle flaky network
    const delay = isNativeRuntime() ? 2000 : 1000;
    await new Promise((r) => setTimeout(r, delay));

    return retryImport(importFn, retriesLeft - 1);
  }
}

